// ============================================================================
// PATIENT 360 HEADER — Cabecera con KPIs, acciones rápidas y navegación
// ============================================================================
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Edit2, Plus, Bell, Phone,
  MessageCircle, Copy, Check, FileText, Building2, Euro, CalendarClock,
  Mail,
} from 'lucide-react';
import { useState } from 'react';
import type { Patient } from '../../types/patient';
import type { PatientSummary } from '../../types/patient';
import { DOCUMENT_TYPE_LABELS } from '../../types/patient';
import { formatPhone } from '../../utils/format';
import { toast } from '../ui/Toast';

interface Patient360HeaderProps {
  patient: Patient;
  summary: PatientSummary | null;
  isActiveInSite: boolean;
  canCreateBudget: boolean;
  prevPatientId: string | null;
  nextPatientId: string | null;
  onEdit: () => void;
  onNewBudget: () => void;
  onNewReminder: () => void;
  onToggleSiteStatus: () => void;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('No se pudo copiar', 'El navegador bloqueó la operación');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
      title={`Copiar ${label.toLowerCase()}`}
      aria-label={`Copiar ${label.toLowerCase()}`}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function Initials({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';

  return (
    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#008DD0] to-[#005a85] flex items-center justify-center text-white font-semibold text-xl shrink-0 shadow-sm">
      {initials}
    </div>
  );
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sublabel?: string;
  tone?: 'default' | 'warning' | 'danger' | 'success';
}

function KpiCard({ icon, label, value, sublabel, tone = 'default' }: KpiCardProps) {
  const toneClasses: Record<string, string> = {
    default: 'bg-gray-50 border-gray-200 text-gray-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    danger: 'bg-red-50 border-red-200 text-red-900',
    success: 'bg-green-50 border-green-200 text-green-900',
  };
  const iconTone: Record<string, string> = {
    default: 'text-gray-400',
    warning: 'text-amber-500',
    danger: 'text-red-500',
    success: 'text-green-500',
  };

  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${toneClasses[tone]}`}>
      <div className={iconTone[tone]}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide opacity-70 truncate">{label}</p>
        <p className="text-sm font-semibold truncate">{value}</p>
        {sublabel && <p className="text-xs opacity-70 truncate">{sublabel}</p>}
      </div>
    </div>
  );
}

export function Patient360Header({
  patient, summary, isActiveInSite, canCreateBudget,
  prevPatientId, nextPatientId,
  onEdit, onNewBudget, onNewReminder, onToggleSiteStatus,
}: Patient360HeaderProps) {
  const navigate = useNavigate();

  const phoneDigits = (patient.phonePrimary || '').replace(/\D/g, '');
  const waUrl = phoneDigits
    ? `https://wa.me/${phoneDigits.startsWith('34') ? phoneDigits : `34${phoneDigits}`}`
    : null;

  const debtFormatted = summary
    ? summary.totalDebt.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    : '—';
  const debtTone: 'default' | 'warning' | 'danger' = !summary
    ? 'default'
    : summary.totalDebt > 1000
      ? 'danger'
      : summary.totalDebt > 0
        ? 'warning'
        : 'default';

  const nextReminderLabel = summary?.nextReminderDate
    ? new Date(summary.nextReminderDate + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })
    : '—';

  return (
    <div className="space-y-4">
      {/* Fila 1: navegación + nombre + badges + acciones */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <button
            onClick={() => navigate('/pacientes')}
            className="p-2 rounded-lg hover:bg-gray-100 shrink-0"
            title="Volver al listado"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>

          <Initials name={patient.fullName} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{patient.fullName}</h1>

              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                patient.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {patient.isActive ? 'Activo' : 'Inactivo global'}
              </span>

              {patient.isActive && !isActiveInSite && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Inactivo en este centro
                </span>
              )}
            </div>

            {/* Datos clave en línea */}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1">
                <FileText className="h-4 w-4 text-gray-400" />
                <span>{DOCUMENT_TYPE_LABELS[patient.documentType]}: <span className="font-medium text-gray-900">{patient.documentNumber}</span></span>
                <CopyButton value={patient.documentNumber} label="Documento" />
              </span>

              {patient.phonePrimary && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{formatPhone(patient.phonePrimary)}</span>
                  <CopyButton value={patient.phonePrimary} label="Teléfono" />
                  <a
                    href={`tel:${patient.phonePrimary}`}
                    className="p-1 text-gray-400 hover:text-[#008DD0] transition-colors"
                    title="Llamar"
                    aria-label="Llamar"
                  >
                    <Phone className="h-3.5 w-3.5" />
                  </a>
                  {waUrl && (
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      title="Abrir en WhatsApp"
                      aria-label="Abrir en WhatsApp"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                    </a>
                  )}
                </span>
              )}

              {patient.email && (
                <span className="inline-flex items-center gap-1">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-900 truncate max-w-[240px]">{patient.email}</span>
                  <CopyButton value={patient.email} label="Email" />
                </span>
              )}

              <span className="text-gray-500">
                {patient.age} años
              </span>
            </div>
          </div>
        </div>

        {/* Acciones + navegación paciente anterior/siguiente */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {/* Navegación ◀ ▶ */}
          <div className="flex items-center gap-1 mr-2 border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => prevPatientId && navigate(`/pacientes/${prevPatientId}`)}
              disabled={!prevPatientId}
              className="p-2 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Paciente anterior"
              aria-label="Paciente anterior"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>
            <div className="h-6 w-px bg-gray-200" />
            <button
              type="button"
              onClick={() => nextPatientId && navigate(`/pacientes/${nextPatientId}`)}
              disabled={!nextPatientId}
              className="p-2 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Paciente siguiente"
              aria-label="Paciente siguiente"
            >
              <ChevronRight className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          {canCreateBudget && (
            <button
              onClick={onNewBudget}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> Nuevo presupuesto
            </button>
          )}

          <button
            onClick={onNewReminder}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Bell className="h-4 w-4" /> Recordatorio
          </button>

          <button
            onClick={onToggleSiteStatus}
            disabled={!patient.isActive}
            className={`px-4 py-2 text-sm font-medium rounded-lg border ${
              !patient.isActive
                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                : isActiveInSite
                  ? 'border-amber-300 text-amber-700 hover:bg-amber-50'
                  : 'border-green-300 text-green-700 hover:bg-green-50'
            }`}
          >
            {isActiveInSite ? 'Dar de baja' : 'Reactivar'}
          </button>

          <button
            onClick={onEdit}
            className="px-4 py-2 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-lg flex items-center gap-2"
          >
            <Edit2 className="h-4 w-4" /> Editar
          </button>
        </div>
      </div>

      {/* Fila 2: KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          icon={<Euro className="h-5 w-5" />}
          label="Deuda total"
          value={debtFormatted}
          sublabel={summary && summary.activeBudgetsCount > 0 ? `${summary.activeBudgetsCount} presup. activos` : undefined}
          tone={debtTone}
        />
        <KpiCard
          icon={<FileText className="h-5 w-5" />}
          label="Presupuestos"
          value={summary?.totalBudgetsCount ?? '—'}
          sublabel={summary?.acceptedBudgetsCount !== undefined ? `${summary.acceptedBudgetsCount} aceptados` : undefined}
        />
        <KpiCard
          icon={<CalendarClock className="h-5 w-5" />}
          label="Próximo recordatorio"
          value={nextReminderLabel}
          sublabel={summary && summary.plannedRemindersCount > 0 ? `${summary.plannedRemindersCount} planificados` : summary?.failedRemindersUnmanaged ? `${summary.failedRemindersUnmanaged} fallidos sin gestionar` : undefined}
          tone={summary?.failedRemindersUnmanaged ? 'warning' : 'default'}
        />
        <KpiCard
          icon={<Building2 className="h-5 w-5" />}
          label="Centros activos"
          value={summary?.activeSitesCount ?? '—'}
          sublabel={summary && summary.totalSitesCount > summary.activeSitesCount ? `${summary.totalSitesCount - summary.activeSitesCount} inactivos` : undefined}
          tone={summary?.activeSitesCount === 0 ? 'danger' : 'default'}
        />
      </div>

      {/* Fila 3: meta info pequeña */}
      {summary?.lastActivityAt && (
        <p className="text-xs text-gray-500">
          Última actividad:{' '}
          <span className="font-medium text-gray-700">
            {new Date(summary.lastActivityAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
          </span>
          {summary.lastActivityLabel && <span> — {summary.lastActivityLabel}</span>}
        </p>
      )}
    </div>
  );
}

export default Patient360Header;
